// DuckDB-WASM の初期化と共通クエリヘルパー。
//
// 役割: 本アプリの唯一の空間エンジンである DuckDB-WASM を起動し、Spatial 拡張を
//       ロードした共有コネクションと、SQL を実行する query() を提供する。
// 関係: duckdb/ 配下の全モジュール（loadFile / toGeoJSON / layer-registry /
//       geo-normalize）と tools/register-tools.js のツールハンドラがここを経由して
//       SQL を実行する。アプリ内で DuckDB に触れる入口はこのファイルに集約する。
//
// 設計メモ: DuckDB-WASM 自体が内部で Web Worker を起動するため、重い空間処理は
//           UI スレッドから分離される（plan.md §2）。独自の Worker 層は設けない。
//
// 流用元: reference/deckgl-duckdb-template/src/duckdb/db.js
import * as duckdb from '@duckdb/duckdb-wasm'

let dbPromise = null
let conn = null

// AsyncDuckDB をシングルトンとして初期化する。
// 多重呼び出しされても初期化は一度だけ行われる。
async function createDb() {
  // jsDelivr から最適なバンドル（exception-handling 対応版など）を選択する。
  const bundles = duckdb.getJsDelivrBundles()
  const bundle = await duckdb.selectBundle(bundles)

  // Worker をワーカースクリプト URL から生成する。
  // Blob 経由にすることで CDN のクロスオリジン制約を回避する。
  const workerUrl = URL.createObjectURL(
    new Blob([`importScripts("${bundle.mainWorker}");`], {
      type: 'text/javascript',
    }),
  )

  const worker = new Worker(workerUrl)
  const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING)
  const db = new duckdb.AsyncDuckDB(logger, worker)
  await db.instantiate(bundle.mainModule, bundle.pthreadWorker)
  URL.revokeObjectURL(workerUrl)

  // Spatial Extension をインストール・ロードする（plan.md §4.1）。
  // ここが失敗すると距離・面積などの空間処理が一切できないため、
  // 起動時の smoke test（App 側）で利用可否を確認する。
  const c = await db.connect()
  await c.query('INSTALL spatial;')
  await c.query('LOAD spatial;')
  conn = c

  return db
}

// 初期化済みの AsyncDuckDB を返す（未初期化なら初期化する）。
export function getDb() {
  if (!dbPromise) {
    dbPromise = createDb()
  }
  return dbPromise
}

// Spatial ロード済みの共有コネクションを返す。
export async function getConnection() {
  await getDb()
  return conn
}

// SQL を実行し、結果を行オブジェクトの配列として返す。
export async function query(sql) {
  const c = await getConnection()
  const result = await c.query(sql)
  return result.toArray().map((row) => row.toJSON())
}
